import { useForm } from '@tanstack/react-form';
import { log } from 'evlog/client';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { z } from 'zod';

import { phoneValidator } from '@virtual-care/isomorphic';
import { Button, CardContent, Checkbox, Label } from '@virtual-care/shadcn';
import {
  AuthLayout,
  FieldMessage,
  FormMessage,
  PhoneInput,
  errorToFormIssue,
} from '@virtual-care/ui';

import { signup } from '../firebase.tsx';

const schema = z.object({
  phoneNumber: z
    .string()
    .min(1, { message: 'shared.validation.phoneRequiredAlt' }),
  terms: z.literal(true, {
    errorMap: () => ({ message: 'shared.validation.termsRequired' }),
  }),
});

const phoneNumberValidator = schema.shape.phoneNumber.superRefine(
  phoneValidator({ validateEmpty: false }),
);

export default function Register() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const form = useForm({
    defaultValues: {
      phoneNumber: import.meta.env.DEV ? '+966512345678' : '+966',
      terms: false,
    },
    onSubmit: async (props) => {
      try {
        log.info({ action: 'signup_submit' });
        await signup(props.value.phoneNumber);
        await navigate('/verify-phone');
        return void 0;
      } catch (error) {
        log.error({ action: 'signup_submit', error });
        return errorToFormIssue(error);
      }
    },
    validators: {
      onSubmitAsync: schema,
    },
  });

  return (
    <AuthLayout
      center
      title={t('auth.signup.title')}
      subtitle={t('auth.signup.subtitle')}
    >
      <CardContent>
        <form
          className="space-y-4"
          onSubmit={async (e) => {
            e.preventDefault();
            e.stopPropagation();
            await form.handleSubmit();
          }}
        >
          <form.Field
            name="phoneNumber"
            validators={{
              onBlurAsync: phoneNumberValidator,
            }}
            children={(field) => (
              <div className="grid w-full items-center gap-2">
                <Label className="text-sm" htmlFor="phone">
                  {t('auth.common.phone')}
                </Label>
                <PhoneInput
                  id="phone"
                  dir="ltr"
                  className="rtl:flex-row-reverse"
                  value={field.state.value}
                  onChange={field.handleChange}
                  onBlur={field.handleBlur}
                  international
                  defaultCountry="SA"
                />
                <FieldMessage errors={field.state.meta.errors} />
              </div>
            )}
          />

          <form.Field
            name="terms"
            children={(field) => (
              <div className="grid w-full items-center gap-2">
                <div className="flex items-start space-x-2">
                  <Checkbox
                    id="terms"
                    checked={field.state.value}
                    onCheckedChange={(it) => field.handleChange(Boolean(it))}
                  />
                  <Label
                    htmlFor="terms"
                    className="text-muted-foreground flex-1 gap-0 text-sm"
                  >
                    {t('auth.signup.agreeTo')}&nbsp;
                    <a
                      href="https://tc.tgl.com.sa/terms-and-privacy"
                      className="text-foreground underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t('auth.signup.termsLinkLabel')}
                    </a>
                  </Label>
                </div>
                <FieldMessage errors={field.state.meta.errors} />
              </div>
            )}
          />
          <form.Subscribe
            selector={(state) => [state.canSubmit, state.isSubmitting]}
            children={([canSubmit, isSubmitting]) => (
              <Button
                id="signup-submit-btn"
                type="submit"
                disabled={!canSubmit}
                className="w-full cursor-pointer"
              >
                {isSubmitting
                  ? t('auth.common.signingIn')
                  : t('auth.signup.submit')}
              </Button>
            )}
          />
          <p className="text-muted-foreground text-sm">
            {t('auth.signup.haveAccount')}&nbsp;
            <Link to="/signin" className="text-foreground">
              {t('auth.signin.title')}
            </Link>
          </p>

          <form.Subscribe
            selector={(state) => state.errors}
            children={(errors) => <FormMessage errors={errors} />}
          />
        </form>
      </CardContent>
    </AuthLayout>
  );
}
